import { createContext, useCallback, useReducer } from "react";

import axios from "../../utils/axios/v1/userAxios";

const SET_INIT = "SET_INIT";
const GET_RISKS = "GET_RISKS";
const GET_SEVERITY_COUNTS = "GET_SEVERITY_COUNTS";
const GET_TOP_ISSUES = "GET_TOP_ISSUES";
const GET_TOP_RESOURCES = "GET_TOP_RESOURCES";
const GET_EXTERNAL_EXPOSURE = "GET_EXTERNAL_EXPOSURE";
const GET_EXTERNAL_EXPOSURE_TREND = "GET_EXTERNAL_EXPOSURE_TREND";
const GET_RISK_DETAILS = "GET_RISK_DETAILS";
const SET_ERROR = "SET_ERROR";
const SET_LOADING = "SET_LOADING";
const SET_FILTERS = "SET_FILTERS";

const initialState = {
  risks: null,
  risksTotal: 0,
  severityCounts: null,
  topIssues: null,
  topResources: null,
  externalExposure: null,
  externalExposureTrend: null,
  riskDetails: null,
  loading: false,
  errMsg: null,
  filters: {
    target_id: null,
    target_ids: null,
    threat_level: null,
    scan_type: null,
    status: null,
    accepted: null,
    cwe_id: null,
    cvss_min: null,
    cvss_max: null,
    search: null,
    first_detected_from: null,
    first_detected_to: null,
    last_detected_from: null,
    last_detected_to: null,
  },
};

const FindingsReducer = (state, action) => {
  switch (action.type) {
    case SET_INIT:
      return {
        ...state,
        risks: null,
        risksTotal: 0,
        severityCounts: null,
        topIssues: null,
        topResources: null,
        externalExposure: null,
        externalExposureTrend: null,
      };
    case GET_RISKS:
      return {
        ...state,
        risks: action.payload.risks,
        risksTotal: action.payload.total,
      };
    case GET_SEVERITY_COUNTS:
      return {
        ...state,
        severityCounts: action.payload.severityCounts,
      };
    case GET_TOP_ISSUES:
      return {
        ...state,
        topIssues: action.payload.topIssues,
      };
    case GET_TOP_RESOURCES:
      return {
        ...state,
        topResources: action.payload.topResources,
      };
    case GET_EXTERNAL_EXPOSURE:
      return {
        ...state,
        externalExposure: action.payload.externalExposure,
      };
    case GET_EXTERNAL_EXPOSURE_TREND:
      return {
        ...state,
        externalExposureTrend: action.payload.externalExposureTrend,
      };
    case GET_RISK_DETAILS:
      return {
        ...state,
        riskDetails: action.payload.riskDetails,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload.loading,
      };
    case SET_FILTERS:
      return {
        ...state,
        filters: action.payload.filters,
      };
    default:
      return state;
  }
};

const FindingsContext = createContext(null);

function FindingsProvider({ children }) {
  const [state, dispatch] = useReducer(FindingsReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({
      type: SET_LOADING,
      payload: { loading },
    });
  }, []);

  const setFilters = useCallback((filters) => {
    dispatch({
      type: SET_FILTERS,
      payload: { filters },
    });
  }, []);

  const getRisks = useCallback(
    async (projectId, siteId, filters = {}, limit = 50, offset = 0, signal) => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (siteId && siteId !== "all") params.append("site_id", siteId);
        if (filters.target_id) params.append("target_id", filters.target_id);
        if (filters.target_ids) params.append("target_ids", filters.target_ids);
        if (filters.threat_level) params.append("threat_level", filters.threat_level);
        if (filters.scan_type) params.append("scan_type", filters.scan_type);
        if (filters.status) params.append("status", filters.status);
        if (filters.accepted !== null && filters.accepted !== undefined) {
          params.append("accepted", filters.accepted);
        }
        if (filters.cwe_id) params.append("cwe_id", filters.cwe_id);
        if (filters.cvss_min !== null && filters.cvss_min !== undefined && filters.cvss_min !== "") params.append("cvss_min", filters.cvss_min);
        if (filters.cvss_max !== null && filters.cvss_max !== undefined && filters.cvss_max !== "") params.append("cvss_max", filters.cvss_max);
        if (filters.search) params.append("search", filters.search);
        if (filters.first_detected_from) params.append("first_detected_from", filters.first_detected_from);
        if (filters.first_detected_to) params.append("first_detected_to", filters.first_detected_to);
        if (filters.last_detected_from) params.append("last_detected_from", filters.last_detected_from);
        if (filters.last_detected_to) params.append("last_detected_to", filters.last_detected_to);
        params.append("limit", limit);
        params.append("offset", offset);

        const response = await axios.get(`dast/risks?${params.toString()}`, { signal });
        
        let risksData = [];
        let totalCount = 0;
        
        if (response.data) {
          if (response.data.success && response.data.data) {
            const dataObj = response.data.data;
            
            if (dataObj.local || dataObj.hostedscan) {
              const localRisks = Array.isArray(dataObj.local) ? dataObj.local : [];
              const hostedscanRisks = Array.isArray(dataObj.hostedscan) ? dataObj.hostedscan : [];
              risksData = [...localRisks, ...hostedscanRisks];
              totalCount = risksData.length;
            }
            else if (dataObj.risks) {
              risksData = Array.isArray(dataObj.risks) ? dataObj.risks : [];
              totalCount = dataObj.total || risksData.length;
            }
            else if (Array.isArray(dataObj)) {
              risksData = dataObj;
              totalCount = dataObj.length;
            }
            else {
              risksData = [];
              totalCount = dataObj.total || 0;
            }
          }
          else if (response.data.risks) {
            risksData = Array.isArray(response.data.risks) ? response.data.risks : [];
            totalCount = response.data.total || risksData.length;
          }
          else if (Array.isArray(response.data)) {
            risksData = response.data;
            totalCount = response.data.length;
          }
          else if (response.data.data) {
            const dataObj = response.data.data;
            
            if (dataObj.local || dataObj.hostedscan) {
              const localRisks = Array.isArray(dataObj.local) ? dataObj.local : [];
              const hostedscanRisks = Array.isArray(dataObj.hostedscan) ? dataObj.hostedscan : [];
              risksData = [...localRisks, ...hostedscanRisks];
              totalCount = risksData.length;
            }
            else if (dataObj.risks) {
              risksData = Array.isArray(dataObj.risks) ? dataObj.risks : [];
              totalCount = dataObj.total || response.data.total || risksData.length;
            }
            else if (Array.isArray(dataObj)) {
              risksData = dataObj;
              totalCount = dataObj.length;
            }
          }
        }
        
        if (!Array.isArray(risksData)) {
          risksData = [];
        }
        
        const transformedRisks = risksData.map((risk) => {
          const riskDefinition = risk.risk_definition || risk.riskDefinition || {};
          
          return {
            id: risk.id,
            target: risk.target || risk.target_label || risk.targetLabel || "-",
            target_id: risk.target_id || risk.targetId,
            targetLabel: risk.targetLabel || risk.target_label || risk.target || "-",
            title: risk.title || riskDefinition.title || "-",
            threat_level: risk.threat_level || risk.threatLevel || riskDefinition.threat_level || riskDefinition.threatLevel || "MEDIUM",
            scan_type: risk.scan_type || risk.scanType || riskDefinition.scan_type || riskDefinition.scanType || null,
            status: risk.status || "OPEN",
            url: risk.url || "",
            reference: risk.reference || "",
            cwe_id: risk.cwe_id || risk.cweId || riskDefinition.cwe_id || riskDefinition.cweId || "",
            cvss_score: risk.cvss_score || risk.cvssScore || riskDefinition.cvss_score || riskDefinition.cvssScore || null,
            accepted: risk.is_accepted || risk.isAccepted || false,
            first_detected_at: risk.first_detected_at || risk.firstDetectedAt,
            last_detected_at: risk.last_detected_at || risk.lastDetectedAt,
            ...risk,
          };
        });
        
        if (totalCount === 0 && transformedRisks.length > 0) {
          totalCount = transformedRisks.length;
        }
        
        dispatch({
          type: GET_RISKS,
          payload: {
            risks: transformedRisks,
            total: totalCount,
          },
        });
        setLoading(false);
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_RISKS,
          payload: {
            risks: [],
            total: 0,
          },
        });
        setLoading(false);
        setErr(err.message);
      }
    },
    [setErr, setLoading]
  );

  const getSeverityCounts = useCallback(
    async (projectId, siteId, signal) => {
      try {
        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (siteId && siteId !== "all") params.append("site_id", siteId);

        const response = await axios.get(`dast/analytics/severity-counts?${params.toString()}`, { signal });
        const apiData = response.data?.data || response.data;
        dispatch({
          type: GET_SEVERITY_COUNTS,
          payload: {
            severityCounts: {
              critical: apiData?.CRITICAL || apiData?.critical || 0,
              high: apiData?.HIGH || apiData?.high || 0,
              medium: apiData?.MEDIUM || apiData?.medium || 0,
              low: apiData?.LOW || apiData?.low || 0,
              informational:
                apiData?.INFORMATIONAL || apiData?.informational || 0,
              total: apiData?.total || 0,
            },
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        // Leave severityCounts null on failure so the UI does not treat an
        // error as "0 vulnerabilities" and hide trend/top resources (LE-433).
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getTopIssues = useCallback(
    async (projectId, siteId, limit = 5, signal) => {
      try {
        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (siteId && siteId !== "all") params.append("site_id", siteId);
        params.append("limit", limit);

        const response = await axios.get(`dast/analytics/top-issues?${params.toString()}`, { signal });
        const apiData = response.data?.data || response.data;
        dispatch({
          type: GET_TOP_ISSUES,
          payload: {
            topIssues: Array.isArray(apiData) ? apiData : (apiData?.issues || []),
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_ISSUES,
          payload: {
            topIssues: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getTopResources = useCallback(
    async (projectId, siteId, limit = 5, signal) => {
      try {
        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (siteId && siteId !== "all") params.append("site_id", siteId);
        params.append("limit", limit);

        const response = await axios.get(`dast/analytics/top-vulnerable-resources?${params.toString()}`, { signal });
        const apiData = response.data?.data || response.data;
        dispatch({
          type: GET_TOP_RESOURCES,
          payload: {
            topResources: Array.isArray(apiData) ? apiData : (apiData?.resources || []),
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_RESOURCES,
          payload: {
            topResources: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getExternalExposure = useCallback(
    async (projectId, siteId, limit = 5, signal) => {
      try {
        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (siteId && siteId !== "all") params.append("site_id", siteId);
        params.append("limit", limit);

        const response = await axios.get(`dast/analytics/external-exposure?${params.toString()}`, { signal });
        const apiData = response.data?.data || response.data;
        dispatch({
          type: GET_EXTERNAL_EXPOSURE,
          payload: {
            externalExposure: Array.isArray(apiData) ? apiData : (apiData?.components || apiData?.exposures || []),
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_EXTERNAL_EXPOSURE,
          payload: {
            externalExposure: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const getExternalExposureTrend = useCallback(
    async (projectId, siteId, days = 7, signal) => {
      try {
        const params = new URLSearchParams();
        if (projectId) params.append("project_id", projectId);
        if (siteId && siteId !== "all") params.append("site_id", siteId);
        params.append("days", days);

        const response = await axios.get(`dast/analytics/external-exposure-trend?${params.toString()}`, { signal });
        const apiData = response.data?.data || response.data;
        dispatch({
          type: GET_EXTERNAL_EXPOSURE_TREND,
          payload: {
            externalExposureTrend: Array.isArray(apiData) ? apiData : (apiData?.trend || apiData?.data || []),
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_EXTERNAL_EXPOSURE_TREND,
          payload: {
            externalExposureTrend: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const acceptRisk = useCallback(
    async (riskId) => {
      try {
        const response = await axios.post(`dast/risks/${riskId}/accept`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const unacceptRisk = useCallback(
    async (riskId) => {
      try {
        const response = await axios.post(`dast/risks/${riskId}/unaccept`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const updateRiskStatus = useCallback(
    async (riskId, status) => {
      try {
        const response = await axios.post(`dast/risks/${riskId}/status`, { status });
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const getRiskDetails = useCallback(
    async (riskId, signal) => {
      try {
        const response = await axios.get(`dast/risks/${riskId}`, { signal });
        const apiData = response.data?.success ? response.data.data : (response.data?.data || response.data);
        dispatch({
          type: GET_RISK_DETAILS,
          payload: {
            riskDetails: apiData,
          },
        });
        return apiData;
      } catch (err) {
        if ("canceled" === err.message) return null;
        dispatch({
          type: GET_RISK_DETAILS,
          payload: {
            riskDetails: null,
          },
        });
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  // ── Finding-level triage (new drawer surface) ───────────────────────────
  //
  // These wrap the CP's `/dast/findings/:finding_id/*` routes, which proxy
  // the scanner's modern finding-dedup endpoints. finding_id is the sha256
  // dedup key the enriched risk mapper now carries; the legacy accept /
  // unaccept / status helpers above still use plugin_id-based risk ids.

  const updateFinding = useCallback(
    async (findingId, body) => {
      try {
        const response = await axios.patch(`dast/findings/${findingId}`, body);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const getFindingOccurrence = useCallback(
    async (findingId, occurrenceId, signal) => {
      try {
        const response = await axios.get(
          `dast/findings/${findingId}/occurrences/${occurrenceId}`,
          { signal }
        );
        return response.data;
      } catch (err) {
        if ("canceled" === err.message) return null;
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const listFindingAcknowledgements = useCallback(
    async (findingId, signal) => {
      try {
        const response = await axios.get(
          `dast/findings/${findingId}/acknowledgements`,
          { signal }
        );
        return response.data;
      } catch (err) {
        if ("canceled" === err.message) return null;
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const createFindingAcknowledgement = useCallback(
    async (findingId, body) => {
      try {
        const response = await axios.post(
          `dast/findings/${findingId}/acknowledgements`,
          body
        );
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  // ── Scan control ──────────────────────────────────────────────────────

  const stopScan = useCallback(
    async (scanId) => {
      try {
        const response = await axios.post(`dast/scans/${scanId}/stop`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  // Returns a URL the browser can navigate to for the HTML report. The CP
  // route streams back with Content-Type text/html so it renders inline.
  const getScanReportUrl = useCallback((scanId) => {
    const base = axios.defaults?.baseURL?.replace(/\/+$/, "") || "";
    return `${base}/dast/scans/${scanId}/report`;
  }, []);

  // ── Suppression rules ────────────────────────────────────────────────

  const listSuppressionRules = useCallback(
    async (params = {}, signal) => {
      try {
        const response = await axios.get("dast/suppression-rules", { params, signal });
        return response.data;
      } catch (err) {
        if ("canceled" === err.message) return null;
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const previewSuppressionRule = useCallback(
    async (body) => {
      try {
        const response = await axios.post("dast/suppression-rules/previews", body);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const createSuppressionRule = useCallback(
    async (body) => {
      try {
        const response = await axios.post("dast/suppression-rules", body);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const revokeSuppressionRule = useCallback(
    async (ruleId) => {
      try {
        const response = await axios.post(`dast/suppression-rules/${ruleId}/revoke`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return null;
      }
    },
    [setErr]
  );

  const initFindings = useCallback(() => {
    dispatch({ type: SET_INIT });
  }, []);

  return (
    <FindingsContext.Provider
      value={{
        ...state,
        setErr,
        setFilters,
        setLoading,
        getRisks,
        getSeverityCounts,
        getTopIssues,
        getTopResources,
        getExternalExposure,
        getExternalExposureTrend,
        getRiskDetails,
        acceptRisk,
        unacceptRisk,
        updateRiskStatus,
        updateFinding,
        getFindingOccurrence,
        listFindingAcknowledgements,
        createFindingAcknowledgement,
        stopScan,
        getScanReportUrl,
        listSuppressionRules,
        previewSuppressionRule,
        createSuppressionRule,
        revokeSuppressionRule,
        initFindings,
      }}
    >
      {children}
    </FindingsContext.Provider>
  );
}

export { FindingsContext, FindingsProvider };
